import { animate, state, style, transition, trigger } from "@angular/animations";
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Input,
	OnInit,
	TemplateRef,
	TrackByFunction,
	inject,
	input,
} from "@angular/core";
import { assertDefined } from "core/functions";
import { PreferencesService } from "core/preferences.service";
import { forkJoin } from "rxjs";
import { map } from "rxjs/operators";
import { NgClass, NgTemplateOutlet } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { ButtonExpandCollapseComponent } from "common/buttons/button-expand-collapse.component";

export interface Group {
	ident: string;
	name?: string;
	nameKey?: string;
	showEmpty: boolean;

	getMemberCount(): number;
}

@Component({
	selector: "stages-common-collapsible-groups",
	templateUrl: "./collapsible-groups.component.html",
	styleUrls: ["./collapsible-groups.component.scss"],
	animations: [
		trigger("expandCollapseFromTop", [
			state("collapsed", style({ "margin-top": "{{contentHeight}}px" }), { params: { contentHeight: 0 } }),
			state("expanded", style({ "margin-top": 0 })),
			transition("collapsed <=> expanded", animate("0.4s 0s cubic-bezier(0.4, 0, 0.2, 1)")),
		]),
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [NgClass, TranslateModule, NgTemplateOutlet, ButtonExpandCollapseComponent],
})
export class CollapsibleGroupsComponent implements OnInit {
	private preferencesService = inject(PreferencesService);
	private changeDetection = inject(ChangeDetectorRef);

	// TODO: Skipped for migration because:
	//  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
	//  and migrating would break narrowing currently.
	@Input() translateNone?: string;
	readonly groups = input.required<Group[]>();
	readonly groupContentTemplate = input.required<TemplateRef<unknown>>();
	readonly preferencesPrefix = input.required<string>();
	readonly defaultOpen = input(false);
	readonly singleGroupCollapsible = input(false);
	readonly groupHeaderTemplate = input<TemplateRef<unknown>>();
	// TODO: Skipped for migration because:
	//  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
	//  and migrating would break narrowing currently.
	@Input() groupHeaderActionMenuTemplate?: TemplateRef<unknown>;
	readonly hoverOnHeader = input(false);
	readonly isEmbeddedInDialog = input(false);

	groupsOpenCloseState: StringToBoolean = {};

	ngOnInit(): void {
		assertDefined(this.preferencesPrefix());
		const groups = this.groups();
		assertDefined(groups);
		assertDefined(this.groupContentTemplate());

		forkJoin(
			groups.map((g) =>
				getPreferencesPromise(g, this.preferencesService, this.defaultOpen(), this.preferencesPrefix()),
			),
		)
			.pipe(
				map((arr) => {
					arr.forEach(([ident, isOpen]) => (this.groupsOpenCloseState[ident] = isOpen));
				}),
			)
			.subscribe(() => {
				this.changeDetection.detectChanges();
			});
	}

	showTranslateNone(groups: Group[]): boolean {
		return groups.reduce((count: number, g: Group) => count + g.getMemberCount(), 0) === 0;
	}

	toggle(group: Group): void {
		this.groupsOpenCloseState[group.ident] = !this.groupsOpenCloseState[group.ident];
		this.preferencesService.setPreference(getPreferencesKey(this.preferencesPrefix(), group), {
			open: this.groupsOpenCloseState[group.ident],
		});
	}

	isGroupOpen(group: Group): boolean {
		if (this.groupsOpenCloseState[group.ident] === undefined) {
			return this.defaultOpen();
		}
		return this.groupsOpenCloseState[group.ident];
	}

	trackBy: TrackByFunction<Group> = (_index, group) => {
		return group.ident;
	};

	getVisibleGroups(groups: Group[]): Group[] {
		return groups.filter((g) => g.showEmpty || g.getMemberCount() > 0);
	}

	getExpandCollapseFromTop(
		group: Group,
		expandable: HTMLDivElement,
	): { value: string; params: Record<string, unknown> } {
		const targetState = this.isGroupOpen(group) ? "expanded" : "collapsed";

		return {
			value: targetState,
			params: {
				contentHeight: 0 - expandable.scrollHeight,
			},
		};
	}
}

async function getPreferencesPromise(
	group: Group,
	preferencesService: PreferencesService,
	defaultValue: boolean,
	preferencesPrefix: string,
): Promise<[string, boolean]> {
	const pref = await preferencesService.getPreference(getPreferencesKey(preferencesPrefix, group), {
		open: defaultValue,
	});
	return [group.ident, pref.open];
}

function getPreferencesKey(preferencesPrefix: string, group: Group): string {
	return preferencesPrefix + "_" + group.ident;
}
